<script lang="ts">
export default {
  name: 'DocumentViewerModal',
}
</script>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import { Download, X, PanelLeftOpen, PanelLeftClose, FileX } from '@lucide/vue';

interface Props {
  show?: boolean;
  fileName: string;
  fetchDocument: (convertToPdf: boolean) => Promise<string>;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  document: null,
  fileName: '',
  fetchDocument: () => Promise.resolve(''),
});

const emit = defineEmits<{
  'update:show': [value: boolean];
  'close': [];
}>();

const dialogRef = ref<HTMLDialogElement | null>(null);
const documentFrame = ref<HTMLIFrameElement | null>(null);
const documentUrl = ref<string | null>(null);
const loading = ref(false);
const documentViewerMode = ref<'split' | 'full'>('full');

const isDocx = computed(() => props.fileName.toLowerCase().endsWith('.docx'));

const open = async () => {
  loading.value = true;
  dialogRef.value?.showModal();
  try {
    documentUrl.value = await props.fetchDocument(true);
    loading.value = false;
  } catch (error) {
    console.error('Error loading document:', error);
    loading.value = false;
  }
};

const close = () => {
  dialogRef.value?.close();
  emit('update:show', false);
  emit('close');

  if (documentUrl.value) {
    URL.revokeObjectURL(documentUrl.value);
    documentUrl.value = null;
  }
};

const downloadDocument = async () => {
  try {
    let url = await props.fetchDocument(false);
    const anchorElement = document.createElement('a');
    anchorElement.href = url;
    anchorElement.download = props.fileName;
    anchorElement.setAttribute('target', '_blank');
    anchorElement.click();
    anchorElement.remove();
  } catch (err) {
    console.error(err);
  }
}

const toggleViewerMode = () => {
  if (documentViewerMode.value !== 'full') {
    documentViewerMode.value = 'full';
  } else {
    documentViewerMode.value = 'split';
  }
}

const onDocumentLoad = () => {
  // Document loaded successfully
};

const onDocumentError = () => {
  console.error('Error loading document in iframe');
  loading.value = false;
};

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.show) {
    close();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleEscapeKey);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscapeKey);
});

watch(() => props.show, (newShow, oldShow) => {
  if (newShow && !oldShow) {
    open();
  }
});

defineExpose({
  open,
  close
});
</script>

<template>
  <Teleport to="body">
    <dialog
      class="modal z-[1001] items-center"
      :class="{ 'modal-open': show, }"
      :style="{ 'width': documentViewerMode === 'split' ? '50vw !important' : '90vw', 'background-color': documentViewerMode !== 'full' ? 'unset !important' : 'unset'}"
      ref="dialogRef"
    >
      <div
        class="modal-box w-[90%] max-w-none h-[90vh] max-h-[90vh] p-0 rounded-xl flex flex-col">

        <div class="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold truncate mr-4">
            {{ props.fileName || 'Document' }}
          </h2>
          <div class="flex items-center gap-2">

            <div class="tooltip tooltip-left" data-tip="Download Microsoft Word format">
              <div
                v-if="isDocx"
                role="button"
                tabindex="0"
                class="btn btn-sm self-center"
                @click="downloadDocument"
              >
                <Download :size="16" /> Download DOCX
              </div>
            </div>

            <div v-if="documentViewerMode === 'full'" class="tooltip tooltip-bottom" data-tip="Move to left half of screen">
              <div
                role="button"
                tabindex="0"
                class="btn btn-sm self-center border-none !bg-transparent"
                @click="toggleViewerMode"
              >
                <PanelLeftClose :size="16" />
              </div>
            </div>
            <div v-else-if="documentViewerMode === 'split'" class="tooltip tooltip-bottom" data-tip="Restore full screen">
              <div
                role="button"
                tabindex="0"
                class="btn btn-sm self-center border-none !bg-transparent"
                @click="toggleViewerMode"
              >
                <PanelLeftOpen :size="16" />
              </div>
            </div>

            <div class="tooltip tooltip-bottom" data-tip="Close">
              <div
                role="button"
                tabindex="0"
                class="ml-2 btn btn-xs h-7 w-9 self-center border-none !bg-transparent"
                @click="close"
              >
                <X :size="16" />
              </div>
            </div>
          </div>
        </div>

        <!-- Document viewer content -->
        <div class="flex-1 overflow-hidden bg-gray-50">
          <div v-if="loading" class="flex flex-col items-center justify-center h-2/3 pb-20">
            <span class="loading loading-dots loading-lg mt-24 text-gray-500"></span>
            <span class="ml-3">Loading document...</span>
          </div>

          <div v-else-if="documentUrl" class="h-full w-full">
            <!-- PDF or document viewer using object/embed -->
            <object
              :data="documentUrl"
              type="application/pdf"
              class="w-full h-full border-0"
              ref="documentFrame"
              @load="onDocumentLoad"
              @error="onDocumentError">
              <embed
                :src="documentUrl"
                type="application/pdf"
                class="w-full h-full border-0" />
              <p class="flex items-center justify-center h-full text-gray-500">
                <span>Your browser doesn't support PDF viewing.
                  <a :href="documentUrl" target="_blank" class="text-blue-500 underline">
                    Click here to download the document
                  </a>
                </span>
              </p>
            </object>
          </div>

          <div v-else class="flex items-center justify-center h-full text-gray-500">
            <div class="text-center">
              <FileX :size="48" class="mb-2 mx-auto" />
              <p>Unable to display document</p>
            </div>
          </div>
        </div>
      </div>
    </dialog>
  </Teleport>
</template>